import Objdict from "objdict"
import Stats from "stats.js"
import Keyb from "keyb"
import Poin from "poin"
import App from "@frog/source/models/App.js"

import unitJobs from "@frog/source/data/unitJobs.js"
import {tileset} from "@frog/source/data/tilesets.js"

import games from "@frog/source/data/games/index.js"
import Unit from "@frog/source/models/Unit.js"
import Delta from "@frog/source/models/Delta.js"
import DevSelection from "@frog/source/models/dev/DevSelection.js"
import Tilesetter from "@frog/source/models/dev/Tilesetter.js"

import Loop from "@frog/source/engine/Loop.js"
//import Audiomix from "@frog/source/engine/Audiomix.js"

import {nanoid} from "nanoid"
import esprima from "esprima-next"

// import Sleep from "@frog/source/utility/Sleep.js"

const dev = new class Dev {
    // === Dev State Management === //
    constructor() {

        this.isAllyDeathEnabled = true
        this.isAllyCollisionEnabled = true

        this.areDevPanelsOpen = false
        this.inProtoMode = false
        this.mode = "battle"

        this.editsSinceLastSave = 0
        this.lastEmptyPositionClicked = undefined
        this.haveProtounitsBeenModified = false
        this.havePlayunitsBeenModified = false

        this.editorDirectory = history.state

        this.selection = {}
        this.devSelection = new DevSelection()
        this.selectedTemplate = undefined

        this.previousProtostagesStack = []
        this.nextProtostagesStack = []
        this.loadedProtostage = undefined
        this.navigatorTab = "chapters"
        this.inspectorTab = "units"
        this.isObjectsFolderExpanded = {
            "units": true,
            "spots": true,
            "scripts": true,
            "encounters": true,
        }
        this.isAssetsFolderExpanded = {
            "units": true,
            "spots": true,
            "scripting": true,
            "zones": true,
            "tiles": true,
        }
        this.tagColors = {}
        this.scriptImportSeparatorStrings = {
            "game": "# ",
            "chapter": "### ",
            "encounter": "#### ",
            "condition": "##### ",
            "comment": "// ",
        }
        this.isRevealingSecretTiles = false

        this.isEnabled = false

        if(window.location.hostname == "localhost"
        || window.location.hostname == "127.0.0.1"
        || window.location.hostname.endsWith(".github.io")
        || window.location.hostname.endsWith(".amazonaws.com")) {
            this.isEnabled = true
            if(window.location.search.includes("production") == false) {
                this.isEnabled = true
            }
            if(window.location.search.includes("playtest") == true) {
                this.inProtoMode = false
                this.areDevPanelsOpen = true
            }
            if(window.location.search.includes("development")) {
                this.inProtoMode = true
                this.areDevPanelsOpen = true
            }
        }

        // this.isEnabled = false

        if(this.isEnabled != true) return

        const devSettingsJSON = localStorage.getItem("devSettings")
        if(devSettingsJSON != undefined) {
            const devSettings = JSON.parse(localStorage.getItem("devSettings"))
            this.mode = devSettings.mode

            if(this.mode == "units"
            || this.mode == "spots") {
                console.warn("locally stored mode preference for the `"
                    + this.mode
                    + "` editor is being deprecated")
                this.mode = "battle"
            }
            if(this.mode == "encounters") {
                console.warn("locally stored mode preference for the `"
                    + this.mode
                    + "` editor is being deprecated")
                this.mode = "geography"
            }

            this.navigatorTab = devSettings.navigatorTab
            this.inspectorTab = devSettings.inspectorTab
            this.isObjectsFolderExpanded = devSettings.isObjectsFolderExpanded || this.isObjectsFolderExpanded
            this.isAssetsFolderExpanded = devSettings.isAssetsFolderExpanded || this.isAssetsFolderExpanded

            this.devSelection.selectedSpotKey = devSettings.selectedSpotKey
            this.devSelection.selectedEncounterKey = devSettings.selectedEncounterKey
            this.devSelection.selectedVertexKey = devSettings.selectedVertexKey
            this.devSelection.selectedSavestateReport = devSettings.selectedSavestateReport
            this.devSelection.selectedSceneKey = devSettings.selectedSceneKey
            this.devSelection.selectedSceneStepKey = devSettings.selectedSceneStepKey
        }

        // Profiling
        this.stats = new Stats()
        this.stats.dom.style.position = "absolute"
        this.stats.dom.style.display = "flex"
        this.stats.dom.style.zIndex = -1
        Object.values(this.stats.dom.children).forEach((child) => child.style = "block")
        this.stats.dom.addEventListener("click", (event) => event.stopPropagation(), true)
        document.body.appendChild(this.stats.dom)

        // // Testing
        // import(/* webpackChunkName: "test.js" */"@frog/source/test.js")
        // import("@frog/source/utility/Testrecord.js")
        // Sleep(20 * 1000).then(() => import(/* webpackChunkName: "test.js" */"@frog/source/test.js"))
        // Sleep(2 * 1000).then(() => import(/* webpackChunkName: "test.js" */"@frog/source/test.js"))
        // require("@frog/source/test.js")
        // Sleep(1000).then(() => import("@frog/source/test.js"))
        // import("@frog/source/test.js")
        // import(/* webpackChunkName: "test.js" */"@frog/source/test.js")
    }
    changeMode(newMode) {
        this.mode = newMode
        if(this.mode == "scripts") {
            this.readScriptFile()
        }
        this.storeDevSettings()
    }
    storeDevSettings() {
        localStorage.setItem(
            "devSettings",
            JSON.stringify({
                "mode": this.mode,
                "navigatorTab": this.navigatorTab,
                "inspectorTab": this.inspectorTab,
                "isObjectsFolderExpanded": this.isObjectsFolderExpanded,
                "isAssetsFolderExpanded": this.isAssetsFolderExpanded,
                "selectedSpotKey": this.devSelection.selectedSpotKey,
                "selectedEncounterKey": this.devSelection.selectedEncounterKey,
                "selectedVertexKey": this.devSelection.selectedVertexKey,
                "selectedSavestateReport": this.devSelection.selectedSavestateReport,
                "selectedSceneKey": this.devSelection.selectedSceneKey,
                "selectedSceneStepKey": this.devSelection.selectedSceneStepKey,
            })
        )
    }
    // === Update Functions === //
    atStartOfLoop() {
        if(this.isEnabled != true) return

        if(this.stats != undefined) {
            this.stats.begin()
        }

        if(this.loadedProtostage == undefined
        && App.game?.slug != undefined
        && games[App.game.slug].chapters[App.game?.chapter?.slug] != undefined) {
            this.loadedProtostage = games[App.game.slug].chapters[App.game.chapter.slug].stage
        }

        if(Keyb.wasJustPressed("`", Loop.delta.ms)) {
            this.areDevPanelsOpen = !this.areDevPanelsOpen
        }
        if(Keyb.wasJustPressed("=", Loop.delta.ms)) {
            this.inProtoMode = !this.inProtoMode
            if(this.inProtoMode == false) {
                this.reloadSavestate("livePlaytest", {"rejectStaleSavestates": true})
            }
        }

        if(this.inProtoMode == true && this.areDevPanelsOpen == false) {
            this.inProtoMode = false
        }

        if(Keyb.wasJustPressed("<escape>", Loop.delta.ms)
        || Keyb.wasJustPressed("<backspace>", Loop.delta.ms)
        || Keyb.wasJustPressed("<delete>", Loop.delta.ms)
        || Poin.wasJustPressed("secondary", true, Loop.delta.ms)) {
            this.selectedTemplate = undefined
            this.devSelection.selectedSpotTemplate = undefined
            this.devSelection.selectedTileTemplate = undefined
            this.devSelection.selectedEncounterAsset = undefined
        }

        if(Keyb.wasJustPressed("<backspace>", Loop.delta.ms)
        || Keyb.wasJustPressed("<delete>", Loop.delta.ms)) {
            this.devSelection.deleteSelection()
        }
    }
    atEndOfLoop() {
        if(this.stats != undefined) {
            if(this.isEnabled != true) return
            this.stats.end()
        }
    }
    // === Filesaving Functions === //
    saveUnitsToFile(options) {
        if(this.loadedProtostage.units == undefined) return

        const isWritten = this.writeToFile({
            "saveObject": Objdict.map(this.loadedProtostage.units, (unit) => Unit.protocopy(unit)),
            "fileName": "units.json",
            "options": options
        })
        if(isWritten == true) {
            this.notification = {
                "message": "Units saved to file",
                "startTime": Date.now(),
            }
        }
        if(this.havePlayunitsBeenModified == true) {
            window.alert("Edits have been made to the playtest units. These will not be saved")
        }
        this.editsSinceLastSave = 0
    }
    autosaveUnitsToFile() {
        if(history.state != null
        && history.state.dataFolder != undefined) {
            //this.saveUnitsToFile()
            localStorage.setItem(
                "workingBackup_" + App.game.slug + "_" + App.game.chapter.slug,
                JSON.stringify(this.loadedProtostage.units, undefined, 4)
            )
        }
        this.editsSinceLastSave += 1
    }
    async pickEditorDirectory(options) {
        options = options || {}

        if(history.state == null
        || history.state.dataFolder == undefined
        || options.isManuallySelectingFile == true) {
            if(this.editorDirectory?.dataFolder != undefined
            && options.isManuallySelectingFile != true) {
                history.replaceState(this.editorDirectory, "loaded games directory")
            } else {
                const filePointer = await window.showDirectoryPicker({
                    "mode": "readwrite"
                })
                this.editorDirectory = {}
                this.editorDirectory.dataFolder = filePointer
                history.replaceState(this.editorDirectory, "loaded games directory")
            }
        }
    }
    async writeToFile({saveObject, rawData, fileName, options}) {
        options = options || {}

        await this.pickEditorDirectory(options)
        const gameslug = options.gameslug || App.game.slug
        const chapterslug = options.chapterslug || App.game.chapter.slug

        const gameFolder = await history.state.dataFolder.getDirectoryHandle(gameslug, {"create": true})
        const chapterFolder = await gameFolder.getDirectoryHandle(chapterslug, {"create": true})
        const fileHandle = await chapterFolder.getFileHandle(fileName, {"create": true})

        if(rawData != undefined) {
            const writable = await fileHandle.createWritable()
            await writable.write(rawData)
            await writable.close()
            return true
        }

        const file = await fileHandle.getFile()
        const text = await file.text()
        if(options.noOverwrite == true
        && text.length > 0) {
            return false
        }

        let newText = JSON.stringify(saveObject, undefined, 4)
        if(newText !== text) {
            const writable = await fileHandle.createWritable()
            await writable.write(newText)
            await writable.close()
            return true
        } else {
            return false
        }

        // If the parent directory does not exist, it will crash:
        // NotFoundError: A requested file or directory could not be found at the time an operation was processed.
        // We should check if the directory exists and make it for them if it doesn't!!
    }
    async saveSavetestToFile(savestate, options) {
        this.notification = {
            "message": "Savetest saved to file",
            "startTime": Date.now(),
        }
        const filename = savestate.memo || savestate.name
        options = options || {}

        await this.pickEditorDirectory(options)
        const gameslug = options.gameslug || App.game.slug
        const chapterslug = options.chapterslug || App.game.chapter.slug

        const gameFolder = await history.state.dataFolder.getDirectoryHandle(gameslug, {"create": true})
        const chapterFolder = await gameFolder.getDirectoryHandle(chapterslug, {"create": true})
        const testFolder = await chapterFolder.getDirectoryHandle("tests", {"create": true})
        const fileHandle = await testFolder.getFileHandle(filename + ".json", {"create": true})
        const writable = await fileHandle.createWritable()
        await writable.write(JSON.stringify(savestate,undefined,4))
        await writable.close()
    }
    saveEnvironmentArtToFile(options) {
        let isWritten = false
        if(this.loadedProtostage.images != undefined) {
            let images = this.loadedProtostage.images
            if(this.loadedProtostage.haveEnvironmentImagesBeenRedrawn == true) {
                images = Tilesetter.drawEnvironment({
                    "world": this.loadedProtostage.world,
                    "isRevealingSecretTiles": false,
                })
            }
            images.forEach((image) => {
                if(image.url == undefined || image.url.startsWith("data:image/png") == false) return
                let fileName = "environment.png"
                if(image.z != undefined) {
                    fileName = "environment" + image.z + ".png"
                    if(image.effect != undefined) {
                        fileName = "environment" + image.z + "-" + image.effect + ".png"
                    }
                }
                isWritten = this.writeToFile({
                    "rawData": convertDataURLtoPNG(image.url),
                    "fileName": fileName,
                    "options": options
                })
            })
        } else if(this.loadedProtostage.image != undefined) {
            if(this.loadedProtostage.image.startsWith("data:image/png") == false) return
            isWritten = this.writeToFile({
                "saveObject": convertDataURLtoPNG(this.loadedProtostage.image),
                "fileName": "environment.png",
                "options": options
            })
        }
        if(isWritten == true) {
            this.notification = {
                "message": "Environment art saved to file",
                "startTime": Date.now(),
            }
        }
    }
    saveTilesToFile(options) {
        const tiles = Object.values(this.loadedProtostage.world.blocks).map((tile) => {
            const distilledTile = structuredClone(tile)
            distilledTile.position = {
                "x": tile.position.x,
                "y": tile.position.y,
                "z": tile.position.z,
            }
            if(distilledTile.tilegid != undefined
            && distilledTile.tileKey != "not-found") {
                delete distilledTile.tilegid
            }
            distilledTile.channelKey = tile.channelKey
            delete distilledTile.size
            delete distilledTile.neighbors
            delete distilledTile.wangEdgeIndex
            delete distilledTile.wangCornerIndex
            if(distilledTile.tileKey == undefined) return null
            return distilledTile
        }).filter((block) => block != null)
        const isWritten = this.writeToFile({
            "saveObject": tiles,
            "fileName": "blocks.json",
            "options": options
        })
        if(isWritten == true) {
            this.notification = {
                "message": "Blocks saved to file",
                "startTime": Date.now(),
            }
        }
    }
    saveEncountersToFile(options) {
        this.notification = {
            "message": "Encounters saved to file",
            "startTime": Date.now(),
        }
        const encounters = Object.values(this.loadedProtostage.world.zones).map((encounter) => {
            if(encounter.points == undefined) {
                encounter.points = [
                    {"x": encounter.x, "y": encounter.y},
                    {"x": encounter.x + encounter.width, "y": encounter.y},
                    {"x": encounter.x + encounter.width, "y": encounter.y + encounter.height},
                    {"x": encounter.x, "y": encounter.y + encounter.height},
                ]
            }
            return {
                "key": encounter.key || encounter.slug,
                "zoomout": encounter.zoomout,
                "z": encounter.z,
                "points": encounter.points.map((point) => {
                    return {
                        "x": point.x,
                        "y": point.y,
                        "key": point.key,
                    }
                })
            }
        })
        const isWritten = this.writeToFile({
            "saveObject": encounters,
            "fileName": "encounters.json",
            "options": options
        })
        if(isWritten == true) {
            this.notification = {
                "message": "Encounter borders saved to file",
                "startTime": Date.now(),
            }
        }
    }
    saveSpotsToFile(options) {
        if(this.loadedProtostage.world.spots == undefined) return

        let spots = this.loadedProtostage.world.spots
        if(Object.values(spots)[0] instanceof Array) {
            const keyedSpots = {}
            Object.values(spots).forEach((spotList) => {
                spotList.forEach((spot) => {
                    const newKey = nanoid().slice(0,5)
                    keyedSpots[newKey] = {
                        "key": newKey,
                        "position": spot.position,
                        "tag": spot.key,
                    }
                })
            })
            spots = keyedSpots
        }
        let spotUnits = Object.values(this.loadedProtostage.units).filter((unit) => {
            return unit.type == "zone" || unit.type == "spot"
        })
        spotUnits.forEach((unit) => {
            spots[unit.key] = {
                "key": unit.key,
                "position": unit.position,
                "tag": unit.tag || unit.intent || unit.templateKey,
            }
        })
        const isWritten = this.writeToFile({
            "saveObject": spots,
            "fileName": "spots.json",
            "options": options
        })
        if(isWritten == true) {
            this.notification = {
                "message": "Spots saved to file",
                "startTime": Date.now(),
            }
        }
    }
    async saveScriptsToFile(options) {
        if(this.loadedScript == undefined) return

        const gameFolder = await history.state.dataFolder.getDirectoryHandle(App.game.slug, {"create": true})
        const chapterFolder = await gameFolder.getDirectoryHandle(App.game.chapter.slug, {"create": true})
        let filePointer = await this.getIndexScriptFileHandle(chapterFolder, {"create": true})

        const writable = await filePointer.createWritable()
        await writable.write(this.loadedScript?.text)
        await writable.close()

        this.notification = {
            "message": "Scripts saved to file",
            "startTime": Date.now(),
        }
    }
    async saveChapterToFolder(options) {
        await this.pickEditorDirectory(options)

        this.saveUnitsToFile(options)
        this.saveEncountersToFile(options)
        this.saveTilesToFile(options)
        this.saveSpotsToFile(options)
        this.saveScriptsToFile(options)
        this.saveEnvironmentArtToFile(options)
    }
    // === Filereading Functions === //
    async reloadTestFiles(options) {
        options = options || {}

        await this.pickEditorDirectory(options)
        const tests = {}
        for await(const gameFolder of this.editorDirectory.dataFolder.values()) {
            if(gameFolder.kind != "directory") continue
            for await(const chapterFolder of gameFolder.values()) {
                if(chapterFolder.kind != "directory") continue
                const testFolder = await chapterFolder.getDirectoryHandle("tests", {"create": true})
                for await(const handle of testFolder.values()) {
                    if(handle.kind != "file") return
                    const file = await handle.getFile()
                    const text = await file.text()

                    const newTest = {
                        "savestate": JSON.parse(text),
                        "filename": file.name,
                    }
                    tests[newTest.savestate.gameSlug + "/" + newTest.savestate.chapterSlug + "/" + ( newTest.savestate.memo || newTest.savestate.name)] = newTest
                }
            }
        }

        const sortedTestsByKey = Object.keys(tests).sort((aKey, bKey) => {
            const a = tests[aKey]
            const b = tests[bKey]

            const gameAlphabetically = a.savestate.gameSlug.localeCompare(b.savestate.gameSlug)
            if(gameAlphabetically != 0) return gameAlphabetically

            const chapterAlphabetically = a.savestate.chapterSlug.localeCompare(b.savestate.chapterSlug)
            if(chapterAlphabetically != 0) return chapterAlphabetically

            const nameAlphabetically = a.filename.localeCompare(b.filename)
            if(nameAlphabetically != 0) return nameAlphabetically

            return 0
        })

        let testsListJSON = "export default testslist = {\n"
        sortedTestsByKey.forEach((key) => {
            const savestate = tests[key].savestate
            const url = "./" + savestate.gameSlug + "/" + savestate.chapterSlug + "/tests/" + tests[key].filename
            testsListJSON += "    \"" + key + "\": require(\"" + url + "\"),\n"
        })
        testsListJSON += "}\n"

        const fileHandle = await this.editorDirectory.dataFolder.getFileHandle("savetests.js", {"create": true})
        const writable = await fileHandle.createWritable()
        await writable.write(testsListJSON)
        await writable.close()
    }
    async getIndexScriptFileHandle(chapterFolder, options) {
        let fileHandle = undefined
        try {
            fileHandle = await chapterFolder.getFileHandle("index.jsx")
        } catch(error) {
            if(error.name == "NotFoundError") {
                fileHandle = await chapterFolder.getFileHandle("index.js", options)
            } else {
                console.error(error)
            }
        }
        return fileHandle
    }
    async readScriptFile(options) {
        options = options || {}

        await this.pickEditorDirectory(options)
        const gameFolder = await history.state.dataFolder.getDirectoryHandle(App.game.slug)
        const chapterFolder = await gameFolder.getDirectoryHandle(App.game.chapter.slug)
        let filePointer = await this.getIndexScriptFileHandle(chapterFolder, {})

        if(options.isManuallySelectingFile == true) {
            filePointer = await window.showOpenFilePicker({
                "types": [
                    {
                        "description": "Chapter JS",
                        "accept": {
                            "text/*": [".js"],
                        },
                    },
                ],
                "excludeAcceptAllOption": true,
                "multiple": false,
            })
            filePointer = filePointer[0]
        }

        const file = await filePointer.getFile()
        const text = await file.text()
        this.parseScript(text)
        return this.loadedScript.scripts
    }
    // === Script Functions === //
    parseScript(text) {
        const parseTree = esprima.parseModule(text, {"range": true, "comment": true, "jsx": true})

        const exportedChapter = parseTree.body.find((declaration) => declaration.type == "ExportDefaultDeclaration")
        var exportedChapterDefinition
        if(exportedChapter.declaration.properties != undefined) {
            exportedChapterDefinition = exportedChapter.declaration
        } else {
            exportedChapterDefinition = parseTree.body.find((declaration) => {
                return declaration.type == "VariableDeclaration"
                    && declaration.declarations[0].id.name == exportedChapter.declaration.name
            }).declarations[0].init
        }
        const parsedStage = exportedChapterDefinition.properties.find((property) => property.key.value == "stage").value
        const parsedConditions = parsedStage.properties.find((property) => property.key.value == "scripts").value.elements

        this.loadedScript = {}
        this.loadedScript.text = text
        this.loadedScript.scripts = parsedConditions
        this.loadedScript.comments = parseTree.comments
        this.loadedScript.scenes = []

        this.sceneDictionary = {}

        let interjectCounter = 0

        let sceneKey = 0
        let sceneStepKey = 0
        parsedConditions.forEach((condition) => {
            sceneKey++
            if(condition.properties != undefined) {
                const parsedSteps = condition.properties.find((property) => property.key.value == "events")
                const steps = parsedSteps.value.elements
                const prefix = this.loadedScript.text.slice(condition.range[0], parsedSteps.range[0])

                const scene = {
                    "range": condition.range,
                    "key": sceneKey + "",
                    "events": {
                        "list": [],
                        "range": parsedSteps.range,
                        "indentLength": prefix.split("").reverse().join("").indexOf("\n"),
                    },
                }

                const commentsInCondition = this.loadedScript.comments.filter((comment) => {
                    return condition.range[0] < comment.range[0]
                        && comment.range[1] < parsedSteps.range[0]
                })
                if(commentsInCondition.length > 0) {
                    const titleRange = commentsInCondition[0].range
                    let range = [titleRange[0] + 2, titleRange[1]]
                    if(text.substring(titleRange[0], titleRange[1]).endsWith("*/")) {
                        range = [titleRange[0] + 2, titleRange[1] - 2]
                    }
                    scene.comment = {
                        "string": commentsInCondition[0].value,
                        "range": range,
                    }
                }

                if(steps == undefined) return
                scene.events.list = convertSyntaxTreeToEventsList(steps, this.loadedScript.comments, parsedSteps.range[0])
                function convertSyntaxTreeToEventsList(steps, comments, rangeStart) {
                    if(steps == undefined) return
                    const eventslist = []

                    steps.map((step, stepIndex) => {
                        const previousStep = stepIndex > 0 ? steps[stepIndex - 1] : {"range": [rangeStart, rangeStart]}
                        const commentsOnStep = comments.filter((comment) => {
                            return previousStep.range[1] < comment.range[0]
                                && comment.range[1] < step.range[0]
                        })
                        if(commentsOnStep.length > 0) {
                            for(const comment of commentsOnStep) {
                                // let range = [comment.range[0] + 2, comment.range[1]]
                                // if(text.substring(comment.range[0], comment.range[1]).endsWith("*/")) {
                                //     range = [comment.range[0] + 2, comment.range[1] - 2]
                                // }
                                eventslist.push({
                                    "type": "comment",
                                    "range": comment.range,
                                    "body": comment.value,
                                    "key": "step" + sceneStepKey,
                                })
                                sceneStepKey++
                            }
                        }

                        if(step.type == "Literal") {
                            sceneStepKey++
                            eventslist.push({
                                "type": "ribbit",
                                "range": step.range,
                                "body": step.value,
                                "key": "step" + sceneStepKey,
                            })
                        } else if(step.type == "TemplateLiteral") {
                            const string = step.quasis[0].value.raw
                            const range = step.quasis[0].range
                            const indentDepth = scene.events.indentLength + 4
                            const body = string.split("\n").map((subline, index) => {
                                if(index == 0) return subline
                                return subline.slice(indentDepth)
                            }).join("\n")
                            sceneStepKey++
                            eventslist.push({
                                "type": "ribbit",
                                "range": range,
                                "body": body,
                                "key": "step" + sceneStepKey,
                            })
                        } else {
                            const codeStep = {
                                "type": "javascript",
                                "range": step.range,
                                "body": text.substring(step.range[0], step.range[1]),
                                "key": interjectCounter,
                            }
                            if(codeStep.body.startsWith("PlotSteps.ifFlagsMeetCondition")) {
                                step.arguments.forEach((parameter) => {
                                    //console.log(parameter, parameter.type, parameter.body?.range)
                                    if(parameter.type == "ArrowFunctionExpression") {
                                        codeStep.condition = text.substring(parameter.body.range[0], parameter.body.range[1])
                                    }
                                    if(parameter.type == "ArrayExpression") {
                                        codeStep.eventsList = convertSyntaxTreeToEventsList(parameter.elements, comments, step.range[0])
                                    }
                                })
                                codeStep.type = "subscene"
                            } else {
                                interjectCounter++
                            }
                            eventslist.push(codeStep)
                        }
                    })
                    return eventslist
                }
                this.loadedScript.scenes.push(scene)
            }
        })
    }
    reloadParsedScriptFromScriptwriter() {
        const scriptWriterNode = document.getElementById("Scriptwriter")
        if(scriptWriterNode == undefined) return

        const sceneEvents = this.devSelection.selectedScene.events
        const recompiledEventsList = this.convertRibbitsToEventsList({
            "textRibbits": scriptWriterNode.innerText.split("\n"),
            "originalEventsList": sceneEvents.list
        })
        const recompiledScene = {
            "list": recompiledEventsList,
            "indentLength": sceneEvents.indentLength,
        }
        const recompiledJS = "\"events\": " + this.recompileSceneEventsToJS(recompiledScene)

        const range = sceneEvents.range
        this.parseScript(
            this.loadedScript.text.substring(0,range[0])
            + recompiledJS
            + this.loadedScript.text.substring(range[1])
        )
    }
    recompileSceneEventsToJS(events) {
        const lines = []

        events.list.forEach((event) => {
            let line
            if(event.type == "subscene") {
                line = "PlotSteps.ifFlagsMeetCondition("
                line += "(flags) => (" + event.condition + "), "
                line += this.recompileSceneEventsToJS({
                    "list": event.eventsList,
                    "indentLength": events.indentLength + 4,
                })
                line +=  "),"
                line = " ".repeat(events.indentLength + 4) + line
            } else if(event.body.includes("\n")) {
                if(event.type == "javascript") {
                    line = event.body + ","
                    line = " ".repeat(events.indentLength + 4) + line
                } else if(event.type == "comment") {
                    line = "/*" + event.body + "*/"
                    line = line.split("\n").map((subline) => {
                        return " ".repeat(events.indentLength + 4) + subline
                    }).join("\n")
                } else if(event.type == "ribbit") {
                    line = "`" + event.body + "`,"
                    line = line.split("\n").map((subline) => {
                        return " ".repeat(events.indentLength + 4) + subline
                    }).join("\n")
                }
            } else {
                if(event.type == "javascript") {
                    line = event.body + ","
                } else if(event.type == "comment") {
                    line = "//" + event.body
                } else if(event.type == "ribbit") {
                    line = "\"" + event.body + "\","
                }
                line = " ".repeat(events.indentLength + 4) + line
            }
            lines.push(line + "\n")
        })

        let returnedValue = "[\n"
        lines.forEach((line) => {
            returnedValue = returnedValue + line
        })
        returnedValue = returnedValue + " ".repeat(events.indentLength) + "]"
        return returnedValue
    }
    convertRibbitsToEventsList({textRibbits, originalEventsList}) {
        const returnedEventsList = []
        let currentEventsList = returnedEventsList
        const conditional = {}
        conditional.subscene = undefined

        let runningBoxRegister = ""
        textRibbits.forEach((lineText) => {
            if(conditional.subscene != undefined) {
                if(lineText.startsWith("    ")) {
                    lineText = lineText.slice(4)
                } else {
                    if(runningBoxRegister.length > 0) {
                        currentEventsList.push({
                            "type": "ribbit",
                            "body": runningBoxRegister,
                        })
                        runningBoxRegister = ""
                    }
                    returnedEventsList.push({
                        "type": "subscene",
                        "condition": conditional.condition,
                        "eventsList": conditional.subscene
                    })
                    currentEventsList = returnedEventsList
                    conditional.subscene = undefined
                }
            }

            if(lineText.trim().startsWith(">> Action") == true) {
                if(runningBoxRegister.length > 0) {
                    currentEventsList.push({
                        "type": "ribbit",
                        "body": runningBoxRegister,
                    })
                    runningBoxRegister = ""
                }
                const code = lineText.split("Action ")[1]
                if(code == null
                || code.trim().length == 0) {
                    return
                }
                currentEventsList.push({
                    "type": "javascript",
                    "body": code,
                })
                return
            } else if(lineText.trim().startsWith("//") == true) {
                if(runningBoxRegister.length > 0) {
                    currentEventsList.push({
                        "type": "ribbit",
                        "body": runningBoxRegister,
                    })
                    runningBoxRegister = ""
                }
                currentEventsList.push({
                    "type": "comment",
                    "body": lineText.slice(2),
                })
                return
            } else if(lineText.includes(":")) {
                if(runningBoxRegister.length > 0) {
                    currentEventsList.push({
                        "type": "ribbit",
                        "body": runningBoxRegister,
                    })
                    runningBoxRegister = ""
                }
                if(lineText.trim().startsWith("if ")) {
                    conditional.subscene = []
                    conditional.condition = lineText.trim().slice(3, lineText.trim().length - 1)
                    currentEventsList = conditional.subscene
                    return
                }
            }
            if(lineText != "") {
                if(runningBoxRegister.length > 0) runningBoxRegister += "\n"
                runningBoxRegister += lineText
            }
        })
        if(runningBoxRegister.length > 0) {
            currentEventsList.push({
                "type": "ribbit",
                "body": runningBoxRegister,
            })
        }

        return returnedEventsList
    }
    // === Clipboard Functions === //
    writeToClipboard(clippedUnit) {
        navigator.permissions.query({ "name": "clipboard-write" }).then((result) => {
            if (result.state === "granted" || result.state === "prompt") {
                navigator.clipboard.writeText(clippedUnit)
            }
        })
    }
    async readFromClipboard() {
        const permissionResult = await navigator.permissions.query({ "name": "clipboard-read" })
        if ((permissionResult.state === "granted" || permissionResult.state === "prompt") == false) {
            return undefined
        }
        return await navigator.clipboard.readText()
    }
    // === Undo-Redo Functions === //
    saveToUndo() {
        if(this.isEditingProto()) {
            this.saveProtostageDataToUndo()
            this.haveProtounitsBeenModified = true
        } else {
            App.game.chapter.saveStage()
            this.havePlayunitsBeenModified = true
        }
    }
    // === Undo Functions
    saveProtostageDataToUndo() {
        this.pushProtostageDataToUndo()
        this.clearProtostageDataToRedo()
    }
    pushProtostageDataToUndo() {
        const protostageData = {
            "units": Objdict.map(this.loadedProtostage.units, (unit) => Unit.protocopy(unit)),
            "spots": this.loadedProtostage.world.spots,
            "zones": this.loadedProtostage.world.zones,
            "blocks": this.loadedProtostage.world.blocks,
        }

        this.previousProtostagesStack.push(structuredClone(protostageData))
        if(this.previousProtostagesStack.length > 20) {
            this.previousProtostagesStack.shift()
        }
    }
    loadProtostageDataToUndo() {
        if(this.previousProtostagesStack.length > 0) {
            this.saveProtostageDataToRedo()
            const poppedprotostageData = this.previousProtostagesStack.pop()
            this.loadedProtostage.units = poppedprotostageData.units
            this.loadedProtostage.world.spots = poppedprotostageData.spots
            this.loadedProtostage.world.zones = poppedprotostageData.zones
            if(JSON.stringify(poppedprotostageData.blocks) != JSON.stringify(this.loadedProtostage.world.blocks)) {
                this.loadedProtostage.world.blocks = poppedprotostageData.blocks
                this.redrawWorld()
            }

            //Audiomix.trigger("UndoAction")
            App.game.chapter.selection.reset()
            App.game.chapter.reloadStage()
        }
    }
    redrawWorld() {
        this.loadedProtostage.images = Tilesetter.drawEnvironment({
            "world": this.loadedProtostage.world,
            "isRevealingSecretTiles": this.isRevealingSecretTiles,
        })
        this.loadedProtostage.haveEnvironmentImagesBeenRedrawn = true
    }
    // === Redo Functions
    saveProtostageDataToRedo() {
        const protostageData = {
            "units": Objdict.map(this.loadedProtostage.units, (unit) => Unit.protocopy(unit)),
            "spots": this.loadedProtostage.world.spots,
            "zones": this.loadedProtostage.world.zones,
            "blocks": this.loadedProtostage.world.blocks,
        }

        this.nextProtostagesStack.push(structuredClone(protostageData))
    }
    loadProtostageDataToRedo() {
        if(this.nextProtostagesStack.length > 0) {
            this.pushProtostageDataToUndo()
            const poppedprotostageData = this.nextProtostagesStack.pop()
            this.loadedProtostage.units = poppedprotostageData.units
            this.loadedProtostage.world.spots = poppedprotostageData.spots
            this.loadedProtostage.world.zones = poppedprotostageData.zones
            if(poppedprotostageData.blocks != this.loadedProtostage.world.blocks) {
                this.loadedProtostage.world.blocks = poppedprotostageData.blocks
                this.redrawWorld()
            }

            //Audiomix.trigger("UndoAction")
            App.game.chapter.selection.reset()
            App.game.chapter.reloadStage()
        }
    }
    clearProtostageDataToRedo() {
        this.nextProtostagesStack = []
    }
    // === Editing Mode Booleans === //
    isFreezingPlaying() {
        return this.inProtoMode
    }
    isEditingProto() {
        return this.inProtoMode
    }
    isInspectorShowing() {
        return this.areDevPanelsOpen
    }
    isPaletteShowing() {
        return this.areDevPanelsOpen
    }
    // === Unit Creation Functions === //
    cacheUnitReferredProperties({unit, player, chapterSlug, gameSlug}) {
        player = player || games[App.game.slug].player
        if(player.party.units[unit.key] != undefined) {
            unit.cachedProperties =  Unit.clean(Unit.copy(player.party.units[unit.key]))
        }
        const jobKey = unit.jobKey || unit.cachedProperties?.jobKey
        if(jobKey != undefined) {
            chapterSlug = chapterSlug || App.game.chapter.slug
            gameSlug = gameSlug || App.game.slug
            const unitJob = unitJobs[gameSlug + "/" + chapterSlug + "/" + jobKey]
                || unitJobs[gameSlug + "/" + jobKey]
                || unitJobs[jobKey]
            if(unitJob != undefined) {
                if(unit.cachedProperties != undefined) {
                    unit.cachedProperties = Objdict.merge(unit.cachedProperties, Unit.clean(Unit.copy(unitJob)))
                } else {
                    unit.cachedProperties = Unit.clean(Unit.copy(unitJob))
                }
            }
        }
    }
    uncacheUnitReferredProperties({unit}) {
        delete unit.cachedProperties
    }
    createNewUnit(newUnit) {
        this.saveToUndo()

        const player = games[App.game.slug].player
        if(player.party.units[newUnit.key] == undefined) {
            newUnit.key = nanoid().slice(0,5)
        }

        if(this.isEditingProto() == true) {
            this.cacheUnitReferredProperties({"unit": newUnit})
            this.loadedProtostage.units[newUnit.key] = newUnit
            this.autosaveUnitsToFile()
        } else {
            const gameSlug  = App.game.chapter.gameslug
            const chapterSlug = App.game.chapter.slug
            App.game.chapter.stage.units[newUnit.key] = Unit.instantiate({
                "unit": newUnit, player, gameSlug, chapterSlug, "random": App.game.chapter.random
            })
            App.game.chapter.recalculateStage()
        }
    }
    // === Cut, Copy, Paste Functions === //
    async clipSelection() {
        const selection = App.game.chapter.selection
        const unitKey = selection.selectedUnitKey || selection.hoveredUnitKey || this.devSelection.listHoveredUnitKey
        let selectedUnit = undefined
        if(this.inProtoMode) {
            if(selection.multiselectedUnitKeys.length > 0) {
                selectedUnit = []
                selection.multiselectedUnitKeys.forEach((key) => {
                    let unit = Unit.protocopy(this.loadedProtostage.units[key])
                    unit.position.x -= selection.position.x
                    unit.position.y -= selection.position.y
                    selectedUnit.push(unit)
                })
            } else {
                selectedUnit = this.loadedProtostage.units[unitKey]
            }
        } else {
            selectedUnit = copyClippedUnit(App.game.chapter.stage.units[unitKey])
        }
        if(selectedUnit != undefined) {
            const stringifiedUnit = JSON.stringify({
                "source": "frogTactics",
                "unit": selectedUnit,
            }, undefined, 4)
            this.writeToClipboard(stringifiedUnit)
            return
        }

        const selectedSpot = this.loadedProtostage.world.spots[this.devSelection.selectedSpotKey] || this.loadedProtostage.world.spots[this.devSelection.hoveredSpotKey] || this.loadedProtostage.world.spots[this.devSelection.listHoveredSpotKey]
        if(selectedSpot != undefined) {
            this.writeToClipboard(JSON.stringify({
                "source": "frogTactics",
                "spot": selectedSpot,
            }, undefined, 4))
            return
        }

        const selectedBlock = this.loadedProtostage.world.blocks[this.devSelection.selectedBlockKeys[0]]
        if(selectedBlock != undefined) {
            this.writeToClipboard(JSON.stringify({
                "source": "frogTactics",
                "tile": selectedBlock,
            }, undefined, 4))
            return
        }
    }
    async pasteSelection() {
        const clipboard = await this.readFromClipboard()
        var parsedClipboard
        try {
            parsedClipboard = JSON.parse(clipboard)
        } catch (error) {
            if(this.mode != "scripts") {
                console.warn("Clipboard does not contain valid JSON")
                console.warn(error)
            }
            return
        }
        if(parsedClipboard.source != "frogTactics") {
            console.warn("Clipboard contains JSON from outside the engine")
            return
        }
        if(this.mode == "battle") {
            if(parsedClipboard.unit != undefined) {
                this.pasteUnit(parsedClipboard.unit)
            } else if(parsedClipboard.spot != undefined) {
                this.pasteSpot(parsedClipboard.spot)
            } else {
                console.warn("Clipboard contains neither a unit nor a spot to paste")
            }
        }
        if(this.mode == "geography") {
            if(parsedClipboard.tile != undefined) {
                const tilesetting = tileset[parsedClipboard.tile.tileKey]
                const tileTemplate = {
                    "key": tilesetting.key,
                    "channelKey": tilesetting.channelKey,
                    "gradientSwap": parsedClipboard.tile.colors || tilesetting.colors,
                }
                this.saveToUndo()
                this.devSelection.addTileToBlock({
                    "position": {
                        "x": App.game.chapter.selection.position.x,
                        "y": App.game.chapter.selection.position.y,
                        "z": this.devSelection.z,
                    },
                    "tileTemplate": tileTemplate,
                })
                this.redrawWorld()
            }
        }
        if(this.mode == "scripts") {
            if(this.devSelection.selectedSceneStep != undefined) {
                // const windowSelection = window.getSelection()
                // const cursorNode = windowSelection.anchorNode
                // const cursorOffset = windowSelection.anchorOffset

                const pastedKey = parsedClipboard.unit.key || parsedClipboard.spot.key
                this.devSelection.selectedSceneStep.body = this.devSelection.selectedSceneStep.body + " " + pastedKey

                // document.getElementById("Scriptwriter").focus()
                // windowSelection.setPosition(cursorNode, cursorOffset)
            }
        }
    }
    pasteSpot(newSpot) {
        const newKey = nanoid().slice(0,5)
        this.saveToUndo()
        this.loadedProtostage.world.spots[newKey] = {
            "position": {
                "x": App.game.chapter.selection.position.x,
                "y": App.game.chapter.selection.position.y,
                "z": 1,
            },
            "key": newKey,
            "tag": newSpot.tag
        }
        if(newSpot.hasCollision !== undefined) {
            this.loadedProtostage.world.spots[newKey].hasCollision = newSpot.hasCollision
        }
        this.devSelection.clearSelected()
    }
    pasteUnit(newUnit) {
        if(newUnit == undefined) return
        const selection = App.game.chapter.selection
        if(newUnit instanceof Array) {
            selection.multiselectedUnitKeys.forEach((unitKey) => {
                selection.modedStage.units[unitKey].previousMultiselect = structuredClone(selection.multiselectedUnitKeys)
            })
            selection.multiselectedUnitKeys = []

            const virtualMultiselectedUnitKeys = []
            newUnit.forEach((unit) => {
                unit.position.x += App.game.chapter.selection.position.x
                unit.position.y += App.game.chapter.selection.position.y
                this.createNewUnit(unit)

                virtualMultiselectedUnitKeys.push(unit.key)
            })
            virtualMultiselectedUnitKeys.forEach((unitKey) => {
                selection.modedStage.units[unitKey].previousMultiselect = structuredClone(virtualMultiselectedUnitKeys)
            })

            //selection.selectedUnitKey = newUnit[0].key
            selection.selectedUnitKey = undefined
            selection.clearSelected()
        } else {
            newUnit.position.x = App.game.chapter.selection.position.x
            newUnit.position.y = App.game.chapter.selection.position.y
            this.createNewUnit(newUnit)

            //selection.selectedUnitKey = newUnit.key
            selection.selectedUnitKey = undefined
            selection.clearSelected()
        }
    }
    // === Savestate Functions === //
    replaySavestate(savestate) {
        this.recordSavestate("back")

        App.game.chapter.restart({
            "randomSeed": savestate.randomSeed,
            "player": savestate.player,
        })
        App.game.chapter.actionTrace = savestate.actionTrace
        App.game.chapter.replaySavestateFromActiontrace(savestate)
        const aSum = savestate.deltastageSum
        const bSum = App.game.chapter.runningDeltastageSum
        const diff = Delta.diffDeltastageSums(aSum, bSum)
        this.devSelection.selectedSavestateReport = {
            "savestate": savestate,
            "endstateFromRecord": aSum,
            "endstateFromActions": bSum,
            "difference": diff,
        }

        if(Object.values(diff).length == 0) {
            this.devSelection.selectedSavestateReport = undefined
            return true
        } else {
            savestate.error = {
                "message": "replay of savestate '" + savestate.name + "' diverged",
                "difference": diff
            }
            console.warn(savestate.error.message, diff)
            return false
        }
    }
    recordSavestate(slug, data) {
        data = data || {}
        if(App.game == undefined) return
        const savestateSlug = "savestate_" + App.game.slug + "_" + App.game.chapter.slug + "_" + slug
        // const savestate = {
        //     "name": slug,
        //     "captureDate": Date.now(),
        //
        //     "gameSlug": App.game.slug,
        //     "chapterSlug": App.game.chapter.slug,
        //     "randomSeed": App.game.chapter.originalSeed,
        //
        //     "player": App.game.player,
        //     "data": data,
        //
        //     "sequenceLengthRemaining": App.sequence?.steps.length,
        //     "actionTrace": App.game.chapter.actionTrace,
        //     "deltastageSum": App.game.chapter.runningDeltastageSum,
        // }
        // // CAUSING AN ERROR
        // try {
        //     localStorage.setItem(
        //         savestateSlug,
        //         JSON.stringify(savestate,undefined,4)
        //     )
        // } catch(error) {
        //     console.log(error)
        // }
        this.notification = {
            "message": "Recorded savestate: " + savestateSlug,
            "startTime": Date.now(),
        }
    }
    // Reload Savestate looks up a savestate in localstorage
    // If it fails to find the requested savestate it will return false
    reloadSavestate(slug, options) {
        const savestateSlug = "savestate_" + App.game.slug + "_" + App.game.chapter.slug + "_" + slug
        const reloadedSavestate = JSON.parse(localStorage.getItem(savestateSlug))
        const FOUR_HOURS = 1000 * 60 * 60 * 4

        options = options || {}
        if(options.rejectStaleSavestates != undefined) {
            if(reloadedSavestate?.captureDate != undefined
            && reloadedSavestate?.captureDate < Date.now() - FOUR_HOURS) {
                return false
            }
        }

        if(reloadedSavestate != undefined) {
            this.replaySavestate(reloadedSavestate)
            return true
        } else {
            console.warn("There is no savestate '" + savestateSlug + "' saved with that name yet")
            return false
        }
    }
    onKeyDown() {
        return async (event) => {
            if(this.isEnabled != true) return
            if(event.metaKey == true
            || event.ctrlKey == true) {
                if(event.shiftKey == true) {
                    if(event.key == "j"
                    || event.key == "k"
                    || event.key == "l") {
                        this.recordSavestate(event.key)
                        event.preventDefault()
                    }
                } else {
                    if(event.key == "j"
                    || event.key == "k"
                    || event.key == "l") {
                        this.reloadSavestate(event.key)
                        event.preventDefault()
                    }
                }
            }
            if(this.inProtoMode != true) return
            if(event.metaKey == true
            || event.ctrlKey == true) {
                if(event.key == "s") {
                    const isManuallySelectingFile = event.shiftKey || false
                    this.saveChapterToFolder({"isManuallySelectingFile": isManuallySelectingFile})
                    event.preventDefault()
                }
                if(event.key == "x") {
                    this.clipSelection()
                    this.devSelection.deleteSelection()
                    event.preventDefault()
                }
                if(event.key == "c") {
                    this.clipSelection()
                    event.preventDefault()
                }
                if(event.key == "v") {
                    this.pasteSelection()
                    event.preventDefault()
                }
                if(event.key == "f") {
                    const element = document.getElementById("search-palette")
                    if(element != undefined) {
                        element.focus()
                    }
                    event.preventDefault()
                }

                if(event.key == "u") {
                    this.changeMode("battle")
                    this.inProtoMode = true
                    event.preventDefault()
                }
                if(event.key == "i") {
                    this.changeMode("geography")
                    this.inProtoMode = true
                    event.preventDefault()
                }
                if(event.key == "o") {
                    this.changeMode("scripts")
                    this.inProtoMode = true
                    event.preventDefault()
                }
            }
            if(this.inProtoMode == true && event.key == "z") {
                if(event.shiftKey == true) {
                    this.loadProtostageDataToRedo()
                } else {
                    this.loadProtostageDataToUndo()
                }
                event.preventDefault()
            }
            if(event.key == "F7") {
                App.game.chapter.actionTrace.push({"type": "killAllEnemies"})
                App.game.chapter.killAllEnemies()
                event.preventDefault()
            }
        }
    }
}

function convertDataURLtoPNG(dataURL) {
    const parts = dataURL.split(",")
    const binaryString = atob(parts[parts.length - 1])
    u8arr = new Uint8Array(binaryString.length)
    for(let counter = binaryString.length; counter >= 0 ; counter--) {
        u8arr[counter] = binaryString.charCodeAt(counter)
    }
    return u8arr
}

// === Dev Key Combinations handler === //
document.addEventListener("keydown", dev.onKeyDown())

export default dev

// === Copying Utilities === //
function copyClippedUnit(unit) {
    if(unit == undefined) return undefined
    return {
        "key": unit.key, // unit's individual unique key
        "position": unit.position && {
            "x": unit.position.x,
            "y": unit.position.y,
            "z": unit.position.z,
        },
        "affiliation": unit.affiliation,
        "jobKey": unit.jobKey,
        "faceKey": unit.faceKey,
        "bodyKey": unit.bodyKey,
        "health": unit.health,
        "maxhealth": unit.maxhealth,
        "name": unit.name,
        "jobName": unit.jobName,
    }
}
